import { type ApolloError, useMutation, useSuspenseQuery } from '@apollo/client'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { useAccessRights } from '@faceup/member'
import { withModalSuspense } from '@faceup/ui'
import type { ModalProps } from '@faceup/ui-base'
import { FrontendPermissionType } from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { getFragmentData, graphql } from '../../__generated__'
import { useMotherId } from '../../providers'
import { AbstractMemberModal } from './AbstractMemberModal'
import { findPermissionByPermissionType } from './AbstractMemberModal/abstractMemberModalHelpers'

const messages = defineMessages('institution.components.memberModal.createMemberModal', [
  { id: 'title', defaultMessage: '' },
  { id: 'emailAlreadyExists', defaultMessage: '' },
] as const)

const fragments = {
  CreateMemberModal_viewer: graphql(`
    fragment CreateMemberModal_viewer on Member {
      id
      isAccountOwner(motherId: $motherId)
      companies(motherId: $motherId) {
        id
      }
      keys {
        id
        permissions(motherId: $motherId) {
          type
          enabled
          additionalData {
            categoryIds
          }
        }
      }
      ...AbstractMemberModal_member
    }
  `),
  CreateMemberModal_institution: graphql(`
    fragment CreateMemberModal_institution on Company {
      id
      organizationalStructure {
        id
      }
      ...AbstractMemberModal_institution
    }
  `),
}

const query = {
  CreateMemberModalData: graphql(`
    query CreateMemberModalDataQuery($motherId: UUID!) {
      memberViewer {
        ...CreateMemberModal_viewer
      }
      institution(organizationalUnitId: $motherId) {
        ...CreateMemberModal_institution
      }
    }
  `),
}

const mutations = {
  CreateMember: graphql(`
    mutation CreateMemberMutation($input: CreateMemberInput!, $motherId: UUID!) {
      createMember(input: $input) {
        createdMember {
          id
          name
          phone
          email

          # need up-to-date count for addMember upsell
          mother(motherId: $motherId) {
            id
            countOfInstitutionMembers(motherId: $motherId)
          }
        }
      }
    }
  `),
}

type CreateMemberMutation = ResultOf<typeof mutations.CreateMember>

type CreateMemberModalProps = {
  onCompleted: (data?: CreateMemberMutation) => void
  onClose: () => void
} & Required<Pick<ModalProps, 'open'>>

const _CreateMemberModal = ({ onCompleted, open, onClose }: CreateMemberModalProps) => {
  const { getMotherId } = useMotherId()
  const accessRights = useAccessRights()
  const { formatMessage } = useIntl()

  const { data } = useSuspenseQuery(query.CreateMemberModalData, {
    variables: { motherId: getMotherId() },
  })

  const memberViewer = getFragmentData(fragments.CreateMemberModal_viewer, data.memberViewer)
  const institution = getFragmentData(fragments.CreateMemberModal_institution, data.institution)

  const [createMember] = useMutation(mutations.CreateMember, {
    onCompleted,
  })

  if (!institution) {
    onClose()
    return null
  }

  const isKredenc = !memberViewer

  return (
    <AbstractMemberModal
      modalVariant='add'
      title={<FormattedMessage message={messages.title} />}
      member={memberViewer ?? null}
      institution={institution}
      open={open}
      onClose={onClose}
      defaultValues={{
        email: '',
        institutionIds: isKredenc
          ? institution.organizationalStructure.map(institution => institution.id)
          : (memberViewer.companies?.map(company => company.id) ?? []),
        permissions: Object.values(FrontendPermissionType).map(type => {
          const enabled = memberViewer?.isAccountOwner || accessRights.isVisibleForPermission[type]

          if (type === FrontendPermissionType.ReportAccess) {
            return {
              type,
              enabled,
              additionalData: isKredenc
                ? {
                    categoryIds: null,
                  }
                : findPermissionByPermissionType(memberViewer.keys?.permissions ?? [], type)
                    ?.additionalData,
            }
          }
          return {
            type,
            enabled,
          }
        }),
      }}
      onSubmit={async (values, { setError }) => {
        try {
          const result = await createMember({
            variables: {
              motherId: getMotherId(),
              input: {
                motherId: getMotherId(),
                companyIds: values.institutionIds ?? [],
                email: values.email,
                permissions: values.permissions,
              },
            },
          })

          if (result.errors) {
            return false
          }

          onClose()
          return true
        } catch (error) {
          if ((error as ApolloError).graphQLErrors?.[0]?.message === 'Member already exists') {
            setError('email', formatMessage(messages.emailAlreadyExists))
          }
          return false
        }
      }}
    />
  )
}

export const CreateMemberModal = withModalSuspense(_CreateMemberModal)
